import { Link, useSearchParams } from "react-router-dom";
import { LoginForm } from "../../../components/login-form";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { motion } from "framer-motion"
import { AlertCircle } from "lucide-react"
import { Alert, AlertDescription } from "../../../components/ui/alert"

const logo = {
  backgroundImage: "url('/logo/logo.png')",
  backgroundSize: 'contain',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
  width: '200px',
  height: '200px',
}

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const logoutReason = searchParams.get('reason');

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 transition-colors duration-300">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50/80 via-blue-50/70 to-indigo-50/80 dark:from-slate-900/80 dark:via-slate-800/70 dark:to-slate-900/80 animate-gradient" />
      <div className="absolute -top-40 -left-40 w-[500px] h-[500px] bg-indigo-200/40 dark:bg-indigo-900/20 rounded-full blur-3xl animate-pulse-slow" />
      <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] bg-blue-200/40 dark:bg-blue-900/20 rounded-full blur-3xl animate-pulse-slow" />

      {/* Content */}
      <div className="relative w-full max-w-md space-y-2 p-4 animate-fadeIn">
        <div className="text-center space-y-4">
          {/* Logo - Centered */}
          <div className="flex justify-center">
            <motion.div
              whileHover={{ scale: 1.1 }}
              transition={{ duration: 0.3 }}
              style={logo}
              className="cursor-pointer"
            />
          </div>

          {/* Title */}
          <h1 className="-mt-15 text-5xl font-extrabold text-slate-800 dark:text-white tracking-tight drop-shadow-md">
            Credex
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400 bg-clip-text text-transparent">
              AI
            </span> 
          </h1>

          <p className="text-slate-600 dark:text-slate-300 text-base max-w-sm mx-auto">
            Intelligent agents for Business Lending & Private Credit — powered
            by next-gen AI.
          </p>
        </div>

        {/* AFK Logout Alert */}
        {logoutReason === 'afk' && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              You were logged out due to inactivity. Please sign in again to continue.
            </AlertDescription>
          </Alert>
        )}

        {/* Login Card */}
        <Card className="shadow-2xl border border-slate-200 dark:border-slate-700 rounded-2xl bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl transform transition-all hover:scale-[1.01] hover:shadow-blue-500/20 dark:hover:shadow-indigo-500/20">
          <CardHeader className="space-y-2">
            <CardTitle className="text-2xl text-center font-bold text-slate-800 dark:text-white">
              Welcome Back
            </CardTitle>
            <CardDescription className="text-center text-slate-600 dark:text-slate-400">
              Sign in to continue your AI-powered journey
            </CardDescription>
          </CardHeader>
<CardContent>
  <LoginForm />
  <div className="mt-2 text-right">
    <Link
      to="/forgot-password"
      className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium transition-all duration-200"
    >
      Forgot Password?
    </Link>
  </div>
</CardContent>

        </Card>

        {/* Footer Links */}
        <div className="flex justify-center space-x-8 text-sm">
          <Link
            to="/about"
            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium transition-all duration-200 hover:underline"
          >
            About
          </Link>
          <Link
            to="/help"
            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium transition-all duration-200 hover:underline"
          >
            Help
          </Link>
        </div>
      </div>
    </div>
  );
}